import { useSession } from "@internal/auth";
import { useEffect, useState } from "react";
import {
  CYCLE_TYPE,
  type CycleData,
  type CycleRecord,
  sortCyclesByStart,
} from "@/lib/cycles";
import { getDbForUser, type PlannerDoc } from "@/lib/db";

function toRecord(json: PlannerDoc): CycleRecord {
  return { id: json.id, data: json.data as unknown as CycleData };
}

/** Every cycle, archived or not, oldest start first. */
export function useAllCycles(): { cycles: CycleRecord[]; loading: boolean } {
  const { user } = useSession();
  const [result, setResult] = useState<{
    cycles: CycleRecord[];
    loading: boolean;
  }>({ cycles: [], loading: true });

  useEffect(() => {
    if (!user) {
      setResult({ cycles: [], loading: false });
      return;
    }
    let cancelled = false;
    let sub: { unsubscribe: () => void } | null = null;
    (async () => {
      const db = await getDbForUser(user.id);
      if (cancelled) return;
      sub = db.daily_planner
        .find({ selector: { user_id: user.id, type: CYCLE_TYPE } })
        .$.subscribe((docs) => {
          const cycles = docs.map((d) => toRecord(d.toJSON() as PlannerDoc));
          setResult({ cycles: sortCyclesByStart(cycles), loading: false });
        });
    })();
    return () => {
      cancelled = true;
      sub?.unsubscribe();
    };
  }, [user]);

  return result;
}

export function useCycle(cycleId: string | undefined): {
  cycle: CycleRecord | null;
  loading: boolean;
} {
  const { user } = useSession();
  const [state, setState] = useState<{
    cycle: CycleRecord | null;
    loading: boolean;
  }>({ cycle: null, loading: true });

  useEffect(() => {
    if (!user || !cycleId) {
      setState({ cycle: null, loading: false });
      return;
    }
    let cancelled = false;
    let sub: { unsubscribe: () => void } | null = null;
    (async () => {
      const db = await getDbForUser(user.id);
      if (cancelled) return;
      sub = db.daily_planner.findOne(cycleId).$.subscribe((doc) => {
        setState({
          cycle: doc ? toRecord(doc.toJSON() as PlannerDoc) : null,
          loading: false,
        });
      });
    })();
    return () => {
      cancelled = true;
      sub?.unsubscribe();
    };
  }, [user, cycleId]);

  return state;
}
